/**
 * Référence des termes de transport international utilisés dans HST.
 * Choix volontairement simplifié pour rester pédagogique (3 Incoterms + 2 régimes TVA).
 */

export const INCOTERMS = {
  EXW: {
    code: 'EXW',
    label: 'Ex Works (Départ usine)',
    short: 'EXW — Départ usine',
    transferPoint: 'À l\'usine du vendeur',
    transporterRisk: 'Minimal — le client gère tout dès le chargement',
    transporterCost: 'Aucun, vous facturez juste le transport',
    teachingNote: 'L\'Incoterm le plus avantageux pour vous : tout le risque et les frais douaniers sont supportés par le destinataire. À privilégier pour les contrats Maroc / Turquie en début d\'activité internationale.',
    color: '#10B981',
  },
  CPT: {
    code: 'CPT',
    label: 'Carriage Paid To (Port payé)',
    short: 'CPT — Port payé',
    transferPoint: 'Au transporteur principal (vous)',
    transporterRisk: 'Modéré — vous restez responsable jusqu\'au lieu convenu',
    transporterCost: 'Frais de transport principal + escale',
    teachingNote: 'Le compromis le plus courant pour l\'intra-UE : vous payez le transport jusqu\'à la destination convenue, mais le risque marchandise passe au client dès le chargement. Pas de TVA française si client UE assujetti.',
    color: '#3B82F6',
  },
  DDP: {
    code: 'DDP',
    label: 'Delivered Duty Paid (Rendu droits payés)',
    short: 'DDP — Rendu droits payés',
    transferPoint: 'À destination, dédouané',
    transporterRisk: 'Maximal — tout est à votre charge jusqu\'à la livraison finale',
    transporterCost: 'Transport + douane + TVA pays destinataire',
    teachingNote: 'L\'Incoterm le plus engageant : vous prenez en charge tous les frais et formalités, douane comprise. Permet de facturer plus cher (premium ~25%) mais nécessite un dédouanement maîtrisé. Réservez aux contrats prestige.',
    color: '#DC2626',
  },
};

export const VAT_REGIMES = {
  domestic: {
    code: 'domestic',
    label: 'TVA France 20%',
    short: 'France 20%',
    rate: 0.20,
    mention: 'TVA française collectée au taux normal',
    teachingNote: 'Régime classique pour les prestations en France métropolitaine : vous facturez 20% de TVA en plus du HT, à reverser au Trésor au prochain trimestre.',
    color: '#1F3864',
  },
  intra: {
    code: 'intra',
    label: 'Autoliquidation intra-UE',
    short: 'Autoliquidation UE',
    rate: 0,
    mention: 'TVA exonérée (Art. 283-2 CGI) — autoliquidation par le preneur identifié à la TVA',
    teachingNote: 'Pour une prestation de transport vers un client professionnel UE assujetti à la TVA : vous facturez sans TVA. Le client autoliquide la TVA dans son pays. Mentionnez "Autoliquidation Art. 283-2 CGI" sur la facture et le n° de TVA intracommunautaire du client.',
    color: '#7E22CE',
  },
  extra: {
    code: 'extra',
    label: 'Exonération export extra-UE',
    short: 'Export Extra-UE',
    rate: 0,
    mention: 'TVA exonérée (Art. 262 CGI) — exportation hors UE',
    teachingNote: 'Pour une livraison hors UE : facturation sans TVA + justificatif douanier (DAU + EUR1 si origine préférentielle). Les frais douaniers (DAU, transit) sont à intégrer dans le coût du contrat.',
    color: '#D97706',
  },
};

// Country code → label + flag emoji for display.
export const COUNTRY_LABELS = {
  FR: { name: 'France', flag: '🇫🇷' },
  DE: { name: 'Allemagne', flag: '🇩🇪' },
  IT: { name: 'Italie', flag: '🇮🇹' },
  ES: { name: 'Espagne', flag: '🇪🇸' },
  BE: { name: 'Belgique', flag: '🇧🇪' },
  NL: { name: 'Pays-Bas', flag: '🇳🇱' },
  LU: { name: 'Luxembourg', flag: '🇱🇺' },
  PT: { name: 'Portugal', flag: '🇵🇹' },
  CH: { name: 'Suisse', flag: '🇨🇭' },
  GB: { name: 'Royaume-Uni', flag: '🇬🇧' },
  MA: { name: 'Maroc', flag: '🇲🇦' },
  TR: { name: 'Turquie', flag: '🇹🇷' },
};

/**
 * Helper: from a contract object, derive its VAT regime ('domestic'|'intra'|'extra').
 * Rules:
 *   - contract.international === 'extra' → extra
 *   - contract.international === 'intra' → intra
 *   - otherwise → domestic
 */
export function getVatRegime(contract) {
  if (!contract) return 'domestic';
  if (contract.international === 'extra') return 'extra';
  if (contract.international === 'intra') return 'intra';
  return 'domestic';
}

/**
 * Helper: compute the effective customs/transit cost for a contract.
 * Returns 0 for domestic + intra-UE contracts.
 */
export function getCustomsCost(contract) {
  if (!contract || contract.international !== 'extra') return 0;
  return contract.customsFees || 0;
}
